import {
  ConfirmModal,
  DetailTitleComponent,
  CustomButton,
  CustomSelect,
  CustomTextarea,
} from '@/shared/ui/components';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { t } from 'ttag';
import AutoCompleteAddress from './autoCompleteAddress';
import DeviceInformation from './deviceInformation';
import MultiEmailInput from './multiEmailInput';
import { useNewRma } from './useNewRma';
import '@/pages/rma-list/ui/rma.scss';

/**
 * The new RMA request page, collecting facility, device RMA details, notification emails, notes and shipping addresses before submission. Reads all state and handlers from useNewRma, locks the facility select for Protect Plus program users or when only one facility exists, and gates submission behind a confirm modal that warns about replacement charges when the facility has no Protect Plus coverage.
 *
 * @returns The new RMA request page markup.
 */
const NewRMA = () => {
  const {
    supportFacilities,
    state,
    setState,
    emailRef,
    supportFacilitiesList,
    isProtectPlusProgram,
    onChangeFacility,
    onChangeEmailList,
    onChangeInputText,
    onInputEmailChange,
    onChangeDataDeviceRMA,
    onChangeDivision,
    onChangeDataAddress,
    handleSubmitRmaRequest,
    initialRmaDevices,
    onClickSubmitRequest,
    updateIsSameShippingAddress,
  } = useNewRma();

  return (
    <>
      <ConfirmModal
        isOpen={state.isOpenConfirmModal}
        onClickLeftButton={() => {
          setState({ isOpenConfirmModal: false });
        }}
        onClickRightButton={() => {
          setState({ isSaving: true });
          handleSubmitRmaRequest();
        }}
        leftButtonName={t`Back`}
        rightButtonName={t`Confirm And Submit`}
        title={t`Alert!`}
        question={
          !state.hasProtectPlus &&
          t`Please note, you may be charged for this RMA. If the RMAed equipment is not returned within 30 days, you will be charged for the replacement cost of the equipment.`
        }
        isSaving={state.isSaving}
      />

      <div className="custom-page rma-detail">
        <div className={classNames('top-container', state.isScroll ? '--has-box-shadow' : '')}>
          <DetailTitleComponent
            isLoadedData={state.isLoadedData}
            linkGoBack="/rma"
            title={t`New RMA request`}
            rightComponent={
              <CustomButton
                id="submit-request-new-rma-btn"
                onClick={onClickSubmitRequest}
                size="small"
              >
                {t`Submit Request`}
              </CustomButton>
            }
          />
        </div>
        <div className="custom-body rma-detail-body">
          <div className="rma-detail-content">
            {(supportFacilities?.length ?? 0) > 0 && (
              <div className="select-facility-input">
                <div className="input-title">{t`Facility`}</div>
                <CustomSelect
                  name="selectedFacility"
                  disabled={isProtectPlusProgram || supportFacilities?.length === 1}
                  options={supportFacilitiesList.current}
                  value={state.selectedFacility}
                  onChange={onChangeFacility}
                  size="middle"
                  allowClear={false}
                  className="select-facility-select"
                  aria-label={t`Facility`}
                />
              </div>
            )}
            <DeviceInformation
              selectedFacility={state.selectedFacility}
              onChangeDataDeviceRMA={onChangeDataDeviceRMA}
              initialRmaDevices={initialRmaDevices}
              isNotEdit={isProtectPlusProgram}
              hasReplaceDevice={state.hasRenewalStock}
            />
            <Row className="rma-detail-content__row">
              <Col span={12} className="mr-3">
                <MultiEmailInput
                  {...({} as any)}
                  ref={emailRef}
                  title={t`Notification email`}
                  onChangeEmailList={onChangeEmailList}
                  onInputChange={onInputEmailChange}
                  isError={!!state.errorObject.invalidEmails}
                  errorMessage={state.errorObject.invalidEmails}
                />
              </Col>
              <Col span={12} className="ml-3">
                <div className="notes-input">
                  <div className="input-title">
                    {t`Notes`}
                    <span className="sub-title">{t`(optional)`}</span>
                  </div>
                  <CustomTextarea
                    placeholder={t`Enter your message`}
                    name="clinicNotes"
                    onChange={onChangeInputText}
                    value={state.clinicNotes}
                  />
                </div>
              </Col>
            </Row>

            <div className="shipping-information">
              <div className="input-title">{t`Shipping Information`}</div>
              <AutoCompleteAddress
                countries={state.countries}
                facilityId={state.selectedFacility?.value}
                onChangeDataAddress={onChangeDataAddress}
                onChangeDivision={onChangeDivision}
                errorObject={state.errorAddress}
                isSameAsShippingAddress
                updateIsSameShippingAddress={updateIsSameShippingAddress}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewRMA;
